// §9 Steps 3 and 4 — one component for both. Verifies against the backend
// (verify-contact); resend calls resend-otp. OTP valid 10 minutes, 5 attempts.
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import OtpInput from '@/components/ui/OtpInput/OtpInput';
import OnboardingLayout from './components/OnboardingLayout';
import StepGuard from './components/StepGuard';
import { useOnboarding } from '@/context/OnboardingContext';
import { OnboardingApiError, resendOtp, verifyOtp } from '@/api/onboarding';
import { maskEmail, maskMobile } from '@/utils/mask';
import { OTP_EXPIRY_SECONDS, OTP_RESEND_SECONDS } from '@/api/constants';
import './onboarding.css';

interface OtpStepProps {
  kind: 'sms' | 'email';
}

const CONFIG = {
  sms: {
    step: 3,
    heading: 'Verify your mobile',
    backTo: '/open-account/personal-details',
    next: '/open-account/email-verification',
    changeLabel: 'Wrong number? Change it',
    target: (mobile: string, email: string) => {
      void email;
      return maskMobile(mobile);
    },
    requires: (d: { personalSaved: boolean }) => d.personalSaved,
  },
  email: {
    step: 4,
    heading: 'Verify your email',
    backTo: '/open-account/sms-verification',
    next: '/open-account/identity-verification',
    changeLabel: 'Wrong email? Change it',
    target: (_mobile: string, email: string) => maskEmail(email),
    requires: (d: { smsVerified: boolean }) => d.smsVerified,
  },
} as const;

const OtpStepForm = ({ kind }: OtpStepProps) => {
  const navigate = useNavigate();
  const { draft, setOtpVerified } = useOnboarding();
  const cfg = CONFIG[kind];
  const target = cfg.target(draft.personal.mobile, draft.personal.email);

  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [checking, setChecking] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [locked, setLocked] = useState(false);
  const [resent, setResent] = useState(false);
  const [resendIn, setResendIn] = useState(OTP_RESEND_SECONDS);
  const submittingRef = useRef(false);

  useEffect(() => {
    const timer = setInterval(() => setResendIn((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(timer);
  }, []);

  const submit = async (value: string) => {
    if (submittingRef.current || locked) return;
    submittingRef.current = true;
    setChecking(true);
    setError('');
    setResent(false);
    try {
      await verifyOtp(kind, value);
      setAccepted(true);
      setOtpVerified(kind);
      setTimeout(() => navigate(cfg.next), 800);
    } catch (err) {
      setCode('');
      const message =
        err instanceof OnboardingApiError
          ? err.message
          : 'The code you entered is incorrect. Please try again.';
      setError(message);
      if (err instanceof OnboardingApiError && err.code === 'AUTH_020') {
        setLocked(true);
      }
    } finally {
      setChecking(false);
      submittingRef.current = false;
    }
  };

  const handleChange = (value: string) => {
    setCode(value);
    setError('');
    if (value.length === 6) {
      void submit(value);
    }
  };

  const handleResend = async () => {
    if (resendIn > 0) return;
    try {
      await resendOtp(kind);
      setLocked(false);
      setError('');
      setCode('');
      setResent(true);
      setResendIn(OTP_RESEND_SECONDS);
    } catch (err) {
      setError(err instanceof OnboardingApiError ? err.message : 'Could not resend the code. Please try again.');
    }
  };

  return (
    <OnboardingLayout step={cfg.step} heading={cfg.heading} backTo={cfg.backTo}>
      {accepted ? (
        <div className="text-center py-5">
          <div className="nova-ob-checkline done justify-content-center" style={{ fontSize: '1.1rem' }}>
            <span aria-hidden="true">✓</span> Code accepted.
          </div>
        </div>
      ) : (
        <>
          <p>We sent a 6-digit code to {target}. Enter it below to continue.</p>

          <OtpInput
            id={`${kind}-otp`}
            value={code}
            onChange={handleChange}
            error={error}
            disabled={checking || locked}
          />

          {checking && (
            <div className="text-center my-3">
              <span className="spinner-border spinner-border-sm" aria-hidden="true" />
            </div>
          )}

          {resent && !error && (
            <div className="text-success mb-2">A new code has been sent.</div>
          )}

          <div className="d-flex justify-content-between align-items-center mt-3">
            <button
              type="button"
              className="btn btn-link p-0"
              disabled={resendIn > 0}
              onClick={handleResend}
            >
              {resendIn > 0 ? `Resend code in ${resendIn}s` : 'Resend code'}
            </button>
            <span className="text-muted" style={{ fontSize: '0.85rem' }}>
              OTP expires in {OTP_EXPIRY_SECONDS}s
            </span>
          </div>

          <button
            type="button"
            className="btn btn-link p-0 mt-3"
            onClick={() => navigate('/open-account/personal-details')}
          >
            {cfg.changeLabel}
          </button>
        </>
      )}
    </OnboardingLayout>
  );
};

export const SmsVerification = () => (
  <StepGuard requires={(d) => CONFIG.sms.requires(d)}>
    <OtpStepForm kind="sms" />
  </StepGuard>
);

export const EmailVerification = () => (
  <StepGuard requires={(d) => CONFIG.email.requires(d)}>
    <OtpStepForm kind="email" />
  </StepGuard>
);
